import uri from "urijs";
import * as jose from 'jose';

import Api, {EventEmitter} from "./api";
import {config} from '../src';

interface OIDCDiscoveryResponse {
	issuer: string,
	authorization_endpoint: string,
	token_endpoint: string,
	userinfo_endpoint: string,
	jwks_uri: string,
}

export interface PkceResponse {
	code: string,
	state: string
}

interface TokenResponse {
	access_token: string;
	id_token: string;
	refresh_token: string;
	expires_in: number;
}

export interface User {
	name: string,
	email: string,
	n8n_token: string,
	picture?: string
}

interface CallbackMessage {
	type: 'oidc_callback';
	state: string;
	code?: string;
	error?: string;
	error_description?: string;
}

const CALLBACK_CHANNEL = 'oidc_callback';

const redirectUri = uri(window.location.origin)
	.path("/callback")
	.toString();

class CallbackEvent extends Event implements PkceResponse {
	constructor(private readonly params: PkceResponse) {
		super('callback')
	}

	get code(): string {
		return this.params.code
	}

	get state() {
		return this.params.state
	}
}

export class LoginEvent extends Event {
	constructor(readonly user: User) {
		super('login');
	}
}

type OnOverloads<R> = {
	<E extends CallbackEvent>(event: 'callback', handler: (e: E) => void): R;
	<E extends LoginEvent>(event: 'login', handler: (e: E) => void): R;
	(event: string, handler: (e: Event) => void): R;
};

export default class KeycloakApi extends Api implements EventEmitter {
	#id?: string;
	#access?: string;
	#refresh?: string;
	#jwks?: jose.JWTVerifyGetKey;

	#target: EventTarget = new EventTarget();

	readonly #override: OIDCDiscoveryResponse;

	#user: User | null = null;

	protected constructor(base: string | uri | URL, discovery: OIDCDiscoveryResponse) {
		super(base instanceof uri ? base : uri(base));
		this.#override = discovery;

		this.on('login', e => this.#user = e.user);
	}

	get user(): User | null {
		return this.#user;
	}

	get access(): string | null {
		return this.#access ?? window.localStorage.getItem('access') ?? null;
	}

	#on(event: string, handler: (e: Event) => void, options?: AddEventListenerOptions): () => void {
		const bound = handler.bind(this);
		this.#target.addEventListener(event, bound, options);
		return () => this.#target.removeEventListener(event, bound, options);
	}

	on: OnOverloads<() => void> = ((event: string, handler: (e: Event) => void) =>
		this.#on(event, handler)) as OnOverloads<() => void>;

	once: OnOverloads<() => void> = ((event: string, handler: (e: Event) => void) =>
		this.#on(event, handler, { once: true })) as OnOverloads<() => void>;

	private static base64UrlRandom(bytes: number): string {
		return crypto.getRandomValues(new Uint8Array(bytes))
			.toBase64({ alphabet: 'base64url', omitPadding: true });
	}

	private static async sha256Challenge(verifier: string): Promise<string> {
		const data = new TextEncoder().encode(verifier);
		const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', data));
		return digest.toBase64({ alphabet: 'base64url', omitPadding: true });
	}

	// async submit(webhook: string, payload: Kickoff) {
	// 	return await fetch(webhook, {
	// 		method: 'POST',
	// 		headers: {
	// 			'Authorization': `Bearer ${this.access}`,
	// 			'Content-Type': 'application/json',
	// 		},
	// 		body: JSON.stringify(payload),
	// 	})
	// 		.then(res => res.json())
	// 		.then(body => {
	//
	// 		});
	// }

	async login(): Promise<User> {
		const existing = await this.trySilentLogin();
		if (existing) {
			this.#target.dispatchEvent(new LoginEvent(existing));
			return existing;
		}

		const jwk = await this.getJwk();
		const token = await this.acquireToken();
		const user = await this.#verify(token, jwk);
		if (!user) throw new Error('Interactive login returned an invalid id_token');

		this.#target.dispatchEvent(new LoginEvent(user));
		return user;
	}

	async #verify(token: string, jwk: jose.JWTVerifyGetKey): Promise<User | null> {
		try {
			return await jose.jwtVerify(token, jwk, {
				issuer: this.#override.issuer,
				audience: config.clientId,
			}).then((token: jose.JWTVerifyResult<jose.JWTPayload>) => token.payload as unknown as User);
		} catch {
			return null; // includes expiry — jose checks `exp` by default
		}
	}

	async #tryRefresh(jwk: jose.JWTVerifyGetKey): Promise<User | null> {
		const refreshToken = window.localStorage.getItem('refresh');
		if (!refreshToken) {
			this.#clearTokens();
			return null;
		}

		try {
			const tokens = await this.#tokenRequest(new URLSearchParams({
				grant_type: 'refresh_token',
				client_id: config.clientId,
				refresh_token: refreshToken,
			}));

			const user = await this.#verify(tokens.id_token, jwk);
			if (!user) throw new Error('Refreshed id_token failed verification');

			this.#persistTokens(tokens);
			return user;
		} catch {
			// refresh token expired/revoked (invalid_grant) or bad response
			this.#clearTokens();
			return null;
		}
	}

	#persistTokens(tokens: TokenResponse): void {
		window.localStorage.setItem('id', tokens.id_token);
		window.localStorage.setItem('access', tokens.access_token);
		// Keycloak rotates refresh tokens by default; keep the old one if omitted.
		if (tokens.refresh_token)
			window.localStorage.setItem('refresh', tokens.refresh_token);
		this.#id = tokens.id_token;
		this.#access = tokens.access_token;
		this.#refresh = tokens.refresh_token;
	}

	#clearTokens(): void {
		window.localStorage.removeItem('id');
		window.localStorage.removeItem('access');
		window.localStorage.removeItem('refresh');
		this.#id = undefined;
		this.#access = undefined;
		this.#refresh = undefined;
	}

	async #tokenRequest(body: URLSearchParams): Promise<TokenResponse> {
		const tokens = await this.fetchDirect(this.#override.token_endpoint, 'POST', {}, body)
			.then(res => res.json()) as TokenResponse & { error?: string; error_description?: string };

		if (tokens.error) throw new Error(`${tokens.error}: ${tokens.error_description}`);
		return tokens;
	}

	async #exchangeCode(code: string, verifier: string): Promise<TokenResponse> {
		return this.#tokenRequest(new URLSearchParams({
			code,
			redirect_uri: redirectUri,
			grant_type: 'authorization_code',
			client_id: config.clientId,
			code_verifier: verifier,
		}));
	}

	async acquireToken(): Promise<string> {
		// Open synchronously so the browser attributes the popup to the click.
		const popup = window.open(
			'about:blank',
			'oidc_login',
			'width=480,height=720,menubar=no,toolbar=no',
		);

		if (!popup) throw new Error('Login popup was blocked by the browser');

		try {
			const state = KeycloakApi.base64UrlRandom(16);
			const nonce = KeycloakApi.base64UrlRandom(16);
			const verifier = KeycloakApi.base64UrlRandom(32);
			const challenge = await KeycloakApi.sha256Challenge(verifier);

			const authUrl = uri(this.#override.authorization_endpoint)
				.addSearch({
					redirect_uri: redirectUri,
					client_id: config.clientId,
					response_type: 'code',
					state,
					nonce,
					scope: 'openid profile email picture roles',
					code_challenge: challenge,
					code_challenge_method: 'S256',
				})
				.toString();

			// Listen before navigating so the message can't be missed.
			const codePromise = this.#waitForCallback(popup, state);
			popup.location.href = authUrl;

			const code = await codePromise;
			const tokens = await this.#exchangeCode(code, verifier);

			// Bind the id_token to our nonce.
			const { payload } = await jose.jwtVerify(tokens.id_token, await this.getJwk(), {
				issuer: this.#override.issuer,
				audience: config.clientId,
			});
			if (payload.nonce !== nonce) throw new Error('OIDC nonce mismatch');

			this.#persistTokens(tokens);
			return tokens.id_token;
		} finally {
			popup.close();
		}
	}

	#waitForCallback(popup: Window, expectedState: string, timeoutMs = 300_000): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			const channel = new BroadcastChannel(CALLBACK_CHANNEL);

			const cleanup = () => {
				channel.close();
				clearInterval(poll);
				clearTimeout(timer);
			};

			channel.onmessage = (e: MessageEvent<CallbackMessage>) => {
				const msg = e.data;
				if (msg?.type !== 'oidc_callback' || msg.state !== expectedState) return;

				cleanup();
				if (msg.error) reject(new Error(`${msg.error}: ${msg.error_description ?? ''}`));
				else if (msg.code) resolve(msg.code);
				else reject(new Error('OIDC callback returned no code'));
			};

			const poll = setInterval(() => {
				if (popup.closed) {
					cleanup();
					reject(new Error('Login window was closed before completing'));
				}
			}, 500);

			const timer = setTimeout(() => {
				cleanup();
				reject(new Error('Login timed out'));
			}, timeoutMs);
		});
	}

	// The popup side calls this from the /callback route.
	static postCallback(search: string = window.location.search): void {
		const params = new URLSearchParams(search);
		const channel = new BroadcastChannel(CALLBACK_CHANNEL);
		channel.postMessage({
			type: 'oidc_callback',
			state: params.get('state') ?? '',
			code: params.get('code') ?? undefined,
			error: params.get('error') ?? undefined,
			error_description: params.get('error_description') ?? undefined,
		} satisfies CallbackMessage);
		channel.close();
		window.close();
	}

	async getJwk(): Promise<jose.JWTVerifyGetKey> {
		if (!this.#jwks)
			this.#jwks = jose.createRemoteJWKSet(new URL(this.#override.jwks_uri));

		return this.#jwks;
	}

	/**
	 * Returns the current user if a valid session can be established without
	 * user interaction (valid stored id_token, or a successful silent refresh),
	 * otherwise null. Never opens a popup.
	 */
	async trySilentLogin(): Promise<User | null> {
		const jwk = await this.getJwk();

		const stored = this.#id ?? window.localStorage.getItem('id') ?? undefined;
		if (stored) {
			const user = await this.#verify(stored, jwk);
			if (user) {
				this.#id = stored;
				return user;
			}
		}

		return this.#tryRefresh(jwk);
	}

	public static async fromOidcDiscovery(discovery: URL): Promise<KeycloakApi> {
		const oidc: OIDCDiscoveryResponse = await fetch(discovery, {
			headers: new Headers({'Accept': 'application/json'}),
		})
			.then(res => res.json());

		return new KeycloakApi(oidc.issuer, oidc);
	}
}